import { FormHelperText, Grid } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import React, { useState, useEffect, ChangeEvent } from 'react'

import { Controller, useFieldArray, useForm } from 'react-hook-form'
import FormControlLabel from '@mui/material/FormControlLabel'

import * as Redux from 'redux'
import { connect } from 'react-redux'
import * as _ from 'lodash'
import clsx from 'clsx'
import RadioGroup from '@mui/material/RadioGroup'
import ClientSafeStorageActions from '@ClientActions/safe-storage.action'
import ClassificationAction from '@ClientActions/classification.action'
import { EClassifications } from '@Enum/classifications.enum'
import { APIServices } from '@Services/api.service'
import { CActions, CNoneOption } from '@Constants/common.constant'
import { RedditTextField } from '@Components/Form/RedditTextField/RedditTextField'
import UploadImage from '@Components/Form/UploadImage/UploadImage'
import PageHeader from '@Components/Pages/PageHeader/PageHeader'
import { CommonService } from '@Services/common.service'
import { FormService } from '@Services/form.service'
import { BaseRadio } from '@Components/Form/BaseRadio/BaseRadio'
import BaseSelect from '@Components/Form/BaseSelect/BaseSelect'
import { MSafeStorage } from '@Models/client/safe-storage.model'
import { MClassification } from '@Models/classification.model'
import { ICommonQuery, IPageHeaderActions } from '@Models/common.model'
import './my-safe-storage.scss'

const defaultValues: any = {
    storages: [
        {
            make: '',
            model: '',
            serialCode: '',
            safeType: '',
            files: {
                fileUrl: '',
                fileId: '',
            },
            isLoading: true,
            disabled: false,
            storageId: '',
        },
    ],
}

// models section
interface IMySafeStorage {
    getSafeStorages: () => void
    addSafeStorage: (safeStorage: any) => Promise<any>
    deleteSafeStorage: (id: string) => Promise<any>
    updateSafeStorages: (safeStorage: any) => Promise<any>
    getStorageTypes: () => void

    getSafeMakes: (params?: ICommonQuery) => void
    getSafeModels: (params?: ICommonQuery, index?: number) => void

    safeStorages: MSafeStorage[]
    userId?: string
    safeMakes: MClassification[]
    safeModels: any[]
    storageTypes: MClassification[]
}

function MySafeStorage(props: IMySafeStorage) {
    // core section
    const {
        getSafeStorages,
        addSafeStorage,
        updateSafeStorages,
        getStorageTypes,

        getSafeMakes,
        getSafeModels,

        deleteSafeStorage,
        safeStorages,
        userId,
        safeMakes,
        safeModels,
        storageTypes,
    } = props
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        register,
        reset,
    } = useForm({ defaultValues })
    const storagesArray = useFieldArray({
        control,
        name: 'storages',
    })
    const watchStorages = watch('storages')

    // states section
    const [headerActions, setHeaderActions] = useState<IPageHeaderActions[]>([
        CActions.edit,
    ])
    const [edited, setEdited] = useState(false)

    // hooks section
    useEffect(() => {
        getSafeStorages()
        getStorageTypes()
        getSafeMakes()

        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    // functions section
    const disabledStorages = (disabled: boolean) => {
        const enabledStorages = safeStorages?.map((item) => {
            return { ...item, disabled }
        })
        const newStorages = { storages: enabledStorages }
        reset(newStorages)
    }

    const setDefaultStorage = (disabled = true) => {
        const defaultStorage = defaultValues?.storages
        defaultStorage[0].isLoading = false
        defaultStorage[0].disabled = disabled
        const newStorages = { storages: [...defaultStorage] }
        reset(newStorages)
    }

    /**
     * reset form when safeStorage changed
     */
    useEffect(() => {
        if (safeStorages?.length) {
            disabledStorages(true)
            setHeaderActions([CActions.edit])
            setEdited(false)
        } else {
            setDefaultStorage(false)
            setEdited(true)
            setHeaderActions([CActions.save])
        }
    }, [safeStorages])

    // functions section
    const handleAddStorage = () => {
        if (
            safeStorages?.length + 1 === watchStorages?.length ||
            (safeStorages?.length === 0 && watchStorages?.length === 1)
        )
            return

        setHeaderActions([CActions.save])
        storagesArray?.append({})
    }

    /**
     * handle form submit and create new/update safeStorage
     * @param formData inputs value
     */
    const onSubmit = (formData: any) => {
        const formEdited = formData?.storages?.filter(
            (item: any) => item?.storageId
        )
        const formAdded = formData?.storages?.filter(
            (item: any) => !item?.storageId
        )
        // setHeaderActions([CDisabledWithLoading.save])
        if (formEdited?.length && !formAdded?.length) {
            const updateStorageBody = formData?.storages?.map(
                (item: any, index: number) => {
                    const updateFiles = [
                        {
                            fileId: item?.files?.fileId,
                            sequence: +index + 1,
                        },
                    ]
                    return {
                        make: item?.make,
                        model: item?.model,
                        serialCode: item?.serialCode,
                        safeType: item?.safeType,
                        files: updateFiles,
                        id: item?.storageId,
                    }
                }
            )
            updateSafeStorages({ safeStorages: updateStorageBody })
                .then(() => {
                    getSafeStorages()
                    setEdited(false)
                    setHeaderActions([CActions.edit])
                })
                .catch(() => {
                    setEdited(false)
                    setHeaderActions([CActions.save])
                })
        } else {
            const { make, model, serialCode, safeType, files } =
                formData?.storages?.[formData?.storages?.length - 1]

            const addStorageBody = {
                userId,
                make,
                model,
                serialCode,
                safeType,
                files: [files] || [],
            }
            addSafeStorage(addStorageBody)
                .then(() => {
                    getSafeStorages()
                    setEdited(false)
                    setHeaderActions([CActions.edit])
                })
                .catch(() => {
                    setEdited(false)
                    setHeaderActions([CActions.save])
                })
        }
    }

    /**
     * handle header action component clicked
     * @param action get from header action component
     */
    const handleHeaderActions = (action?: string) => {
        switch (action) {
            case 'save':
                handleSubmit(onSubmit)()
                break
            case 'edit': {
                setHeaderActions([CActions.save])
                if (safeStorages?.length) {
                    disabledStorages(false)
                } else {
                    setDefaultStorage(false)
                }
                setEdited(true)
                break
            }

            default:
                break
        }
    }

    const handleFileChanged = (index: number) => (file: any) => {
        setValue(`storages.${+index}.isLoading`, true)
        CommonService.uploadImage(file)
            .then((response: any) => {
                const imageFile: any = {
                    fileId: response?.id,
                    sequence: index,
                    fileUrl: response?.url,
                }
                setValue(`storages.${+index}.isLoading`, false)
                setValue(`storages.${+index}.files`, imageFile, {
                    shouldValidate: true,
                })
            })
            .catch(() => {
                setValue(`storages.${+index}.isLoading`, false)
            })
    }

    const handleDeleteStorage = (index: number, storageId: string) => {
        if (!storageId) {
            return storagesArray.remove(index)
        }
        deleteSafeStorage(storageId).then(() => {
            setHeaderActions([CActions.edit])
        })
    }

    const handleMakeChange = (e: ChangeEvent<any>, index: number) => {
        const parentCode = e?.target?.value
        setValue(`storages.${index}.model`, '')
        getSafeModels({ parentCode }, index)
    }

    // elements section
    const storageElement = storagesArray.fields.map((storage: any, index) => {
        // register field manually
        register(`storages.${index}.files.fileUrl`, { required: true })

        return (
            <React.Fragment key={storage?.id}>
                <Grid container spacing={3}>
                    <Grid item md={4} sm={12} xs={12}>
                        <div>
                            <Controller
                                control={control}
                                name={`storages.${index}.make`}
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <BaseSelect
                                        {...field}
                                        label="Make"
                                        className="mb-2"
                                        value={value}
                                        onChange={(e: ChangeEvent<unknown>) => {
                                            onChange(e)
                                            handleMakeChange(e, index)
                                        }}
                                        options={safeMakes}
                                        fullWidth
                                        error={
                                            errors?.storages?.[index]?.make &&
                                            true
                                        }
                                        helperText={FormService.getErrorMessage(
                                            errors?.storages?.[index],
                                            'make',
                                            'Make'
                                        )}
                                        disabled={
                                            watchStorages?.[index]?.disabled
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={`storages.${index}.model`}
                                defaultValue=""
                                // rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <BaseSelect
                                        {...field}
                                        value={value}
                                        onChange={onChange}
                                        options={
                                            safeModels?.[index]?.length
                                                ? safeModels?.[index]
                                                : [CNoneOption]
                                        }
                                        label="Model"
                                        fullWidth
                                        className="reddit-input mb-2 w-100"
                                        error={
                                            errors?.storages?.[index]?.model &&
                                            true
                                        }
                                        helperText={FormService.getErrorMessage(
                                            errors?.storages?.[index],
                                            'model',
                                            'Model'
                                        )}
                                        disabled={
                                            watchStorages?.[index]?.disabled
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={`storages.${index}.serialCode`}
                                defaultValue=""
                                rules={{ required: true }}
                                render={({
                                    field: { ref, value, onChange, ...field },
                                }: any) => (
                                    <RedditTextField
                                        {...field}
                                        value={value || ''}
                                        onChange={onChange}
                                        label="Serial #"
                                        variant="filled"
                                        className="reddit-input mb-2 w-100"
                                        error={
                                            errors?.storages?.[index]
                                                ?.serialCode && true
                                        }
                                        helperText={FormService.getErrorMessage(
                                            errors?.storages?.[index],
                                            'serialCode',
                                            'Serial #'
                                        )}
                                        disabled={
                                            watchStorages?.[index]?.disabled
                                        }
                                    />
                                )}
                            />
                        </div>
                        <div className="page-gray-card p-3">
                            <p className="gray-text m-0">Safe Type</p>
                            <div style={{ marginLeft: '-10px' }}>
                                <div className="d-flex align-items-center">
                                    <Controller
                                        control={control}
                                        name={`storages.${index}.safeType`}
                                        rules={{ required: true }}
                                        defaultValue={null}
                                        render={({
                                            field: {
                                                ref,
                                                value,
                                                onChange,
                                                ...field
                                            },
                                        }: any) => (
                                            <RadioGroup
                                                aria-label="safeType"
                                                value={value}
                                                onChange={onChange}
                                            >
                                                {storageTypes?.map(
                                                    (safeType) => {
                                                        return (
                                                            <FormControlLabel
                                                                key={
                                                                    safeType?.value
                                                                }
                                                                control={
                                                                    <BaseRadio
                                                                        value={
                                                                            safeType?.value
                                                                        }
                                                                        disabled={
                                                                            watchStorages?.[
                                                                                index
                                                                            ]
                                                                                ?.disabled
                                                                        }
                                                                    />
                                                                }
                                                                label={
                                                                    <span className="gray-text">
                                                                        {
                                                                            safeType?.name
                                                                        }
                                                                    </span>
                                                                }
                                                                className="m-0"
                                                            />
                                                        )
                                                    }
                                                )}

                                                {errors?.storages?.[index]
                                                    ?.safeType &&
                                                    true && (
                                                        <FormHelperText
                                                            error
                                                            className="ml-3"
                                                        >
                                                            {FormService.getErrorMessage(
                                                                errors
                                                                    ?.storages?.[
                                                                    index
                                                                ],
                                                                'safeType',
                                                                'Safe Type'
                                                            )}
                                                        </FormHelperText>
                                                    )}
                                            </RadioGroup>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12}>
                        <UploadImage
                            className={clsx(
                                errors?.storages?.[index]?.files &&
                                    true &&
                                    'border-danger'
                            )}
                            style={{ height: 170 }}
                            fileChanged={handleFileChanged(index)}
                            imageUrl={watchStorages?.[index]?.files?.fileUrl}
                            isLoading={watchStorages?.[index]?.isLoading}
                            id={`upload-image-${index}`}
                            disabled={watchStorages?.[index]?.disabled}
                        />
                    </Grid>
                    {!watchStorages?.[index]?.disabled &&
                    safeStorages?.length ? (
                        <Grid item md={4} sm={12} xs={12}>
                            <div className="action-container d-flex justify-content-end">
                                <span
                                    className="delete-action"
                                    onClick={() =>
                                        handleDeleteStorage(
                                            index,
                                            storage?.storageId
                                        )
                                    }
                                >
                                    {watchStorages?.[index]?.storageId
                                        ? 'Delete'
                                        : 'Remove'}
                                </span>
                            </div>
                        </Grid>
                    ) : null}
                </Grid>
                <div className="page-divider my-3" />
            </React.Fragment>
        )
    })

    // main section
    return (
        <div className="page-my-safe-storge">
            <div className="page-min-height">
                <PageHeader
                    title="My Safe Storage"
                    actions={headerActions}
                    getActions={handleHeaderActions}
                />
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    {storageElement}
                </form>
                {!edited && (
                    <div>
                        <span
                            className="cursor-pointer"
                            onClick={handleAddStorage}
                        >
                            <AddCircleOutlineIcon className="add-icon mr-2" />
                            <span className="gray-text">Add Another</span>
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { safeStorages, safeMakes, safeModels } = store?.client?.safeStorages
    const { id } = store?.auth?.user
    const { storageTypes } = store?.client.classifications
    return {
        safeStorages,
        userId: id,
        safeMakes,
        safeModels,
        storageTypes,
    }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const {
        getSafeStorages,
        addSafeStorage,
        deleteSafeStorage,
        updateSafeStorages,
        getSafeClassification,
    } = ClientSafeStorageActions
    const { getClassifications } = ClassificationAction
    const { SAFE_MAKE, SAFE_MODEL, STORAGE_TYPE } = EClassifications
    return {
        getSafeStorages: () => dispatch(getSafeStorages() as any),
        addSafeStorage: (payload: any) =>
            dispatch(addSafeStorage(payload) as any),
        getStorageTypes: () =>
            dispatch(getClassifications(STORAGE_TYPE) as any),
        deleteSafeStorage: (id: string) =>
            dispatch(deleteSafeStorage(id) as any),
        updateSafeStorages: (payload: any) =>
            dispatch(updateSafeStorages(payload) as any),

        getSafeMakes: (params?: ICommonQuery) =>
            dispatch(
                getSafeClassification({ ...params, type: SAFE_MAKE }) as any
            ),
        getSafeModels: (params?: ICommonQuery, index?: number) =>
            dispatch(
                getSafeClassification(
                    { ...params, type: SAFE_MODEL },
                    index
                ) as any
            ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MySafeStorage)
